import React from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const WorkflowProgress = () => {
  const location = useLocation();

  const workflowSteps = [
    {
      id: 'selection',
      label: 'Select Playlists',
      path: '/playlist-selection-sync-setup',
      icon: 'List',
      description: 'Choose playlists to sync'
    },
    {
      id: 'preview',
      label: 'Preview & Configure',
      path: '/sync-preview-configuration',
      icon: 'Settings',
      description: 'Review sync settings'
    },
    {
      id: 'progress',
      label: 'Sync Progress',
      path: '/sync-progress-real-time-tracking',
      icon: 'RefreshCw',
      description: 'Monitor sync status'
    }
  ];

  const isWorkflowRoute = workflowSteps.some(step => step.path === location.pathname);

  if (!isWorkflowRoute) {
    return null;
  }

  const getCurrentStepIndex = () => {
    return workflowSteps.findIndex(step => step.path === location.pathname);
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="sticky top-14 lg:top-16 z-50 bg-background border-b border-border shadow-elevation-1">
      <div className="px-4 lg:px-6 py-4">
        {/* Mobile Progress Indicator */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-text-primary">
              Step {currentStepIndex + 1} of {workflowSteps.length}
            </h2>
            <span className="text-xs text-text-secondary">
              {Math.round(((currentStepIndex + 1) / workflowSteps.length) * 100)}% Complete
            </span>
          </div>
          
          <div className="flex items-center space-x-2 mb-2">
            <Icon 
              name={workflowSteps[currentStepIndex]?.icon} 
              size={16} 
              className="text-primary" 
            />
            <span className="text-sm font-medium text-text-primary">
              {workflowSteps[currentStepIndex]?.label}
            </span>
          </div>
          
          <div className="w-full bg-surface-secondary rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${((currentStepIndex + 1) / workflowSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Desktop Progress Steps */}
        <div className="hidden lg:block">
          <div className="flex items-center justify-between">
            {workflowSteps.map((step, index) => {
              const isActive = index === currentStepIndex;
              const isCompleted = index < currentStepIndex;
              const isUpcoming = index > currentStepIndex;

              return (
                <div key={step.id} className="flex items-center">
                  {/* Step Circle */}
                  <div className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                        isCompleted
                          ? 'bg-success border-success text-success-foreground'
                          : isActive
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'bg-surface border-border text-text-secondary'
                      }`}
                    >
                      {isCompleted ? (
                        <Icon name="Check" size={20} />
                      ) : (
                        <Icon name={step.icon} size={20} />
                      )}
                    </div>
                    
                    {/* Step Info */}
                    <div className="ml-4">
                      <div
                        className={`text-sm font-medium transition-colors duration-200 ${
                          isActive
                            ? 'text-primary'
                            : isCompleted
                            ? 'text-success' :'text-text-secondary'
                        }`}
                      >
                        {step.label}
                      </div>
                      <div className="text-xs text-text-muted mt-1">
                        {step.description}
                      </div>
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < workflowSteps.length - 1 && (
                    <div className="flex-1 mx-8">
                      <div
                        className={`h-0.5 transition-colors duration-300 ${
                          index < currentStepIndex ? 'bg-success' : 'bg-border'
                        }`}
                      ></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-text-muted mb-2">
              <span>Progress</span>
              <span>{Math.round(((currentStepIndex + 1) / workflowSteps.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-surface-secondary rounded-full h-1">
              <div 
                className="bg-primary h-1 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((currentStepIndex + 1) / workflowSteps.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowProgress;